/**
 * Admin Dashboard — Sidebar-based admin panel
 *
 * Layout:
 *   - Fixed left sidebar (260px expanded / 68px collapsed)
 *   - Main content area shifts right dynamically
 *   - Mobile: slide-in drawer
 *   - Collapse preference stored in localStorage
 *
 * All tab content components are unchanged — rendered conditionally
 * based on activeTab state instead of inside <Tabs>.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, UserCheck, UserX, CreditCard, BookOpen, Wallet } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

import {
  API_BASE, getAdminToken, getAdminRole, getAdminName, colorMap,
} from '../components/admin/adminUtils';
import { AdminSidebar, MobileMenuButton } from '../components/admin/AdminSidebar';

// Tab content components — all unchanged
import { UsersTab }               from '../components/admin/UsersTab';
import { PaymentsTab }            from '../components/admin/PaymentsTab';
import { EnrollTab }              from '../components/admin/EnrollTab';
import { RevenueTab }             from '../components/admin/RevenueTab';
import { CoursesTab }             from '../components/admin/CoursesTab';
import { ServerTab }              from '../components/admin/ServerTab';
import { ActivityTab }            from '../components/admin/ActivityTab';
import { ProjectsTab }            from '../components/admin/ProjectsTab';
import { AnnouncementTab }        from '../components/admin/AnnouncementTab';
import { TicketsTab }             from '../components/admin/TicketsTab';
import { AgentsTab }              from '../components/admin/AgentsTab';
import { SecurityTab }            from '../components/admin/SecurityTab';
import { NetworkTab }             from '../components/admin/NetworkTab';
import { AnalyticsTab }           from '../components/admin/AnalyticsTab';
import { PromoPosterTab }         from '../components/admin/PromoPosterTab';
import { AchievementPosterTab }   from '../components/admin/AchievementPosterTab';
import { BlueprintTab }           from '../components/admin/BlueprintTab';
import { BroadcastTab }           from '../components/admin/BroadcastTab';
import { EmailActivityTab }       from '../components/admin/EmailActivityTab';
import { NewCourseGuideTab }      from '../components/admin/NewCourseGuideTab';
import { HealthMonitorTab }       from '../components/admin/HealthMonitorTab';
import { CertificateCatalogTab }  from '../components/admin/CertificateCatalogTab';
import { JobsTab }                from '../components/admin/JobsTab';
import { PerformanceDiagnosticTab } from '../components/admin/PerformanceDiagnosticTab';
import { ProfileStatsTab }        from '../components/admin/ProfileStatsTab';
import { TeamTab }                from '../components/admin/TeamTab';
import { EmailBroadcastTab }      from '../components/admin/EmailBroadcastTab';
import { ExamManagementTab }      from '../components/admin/ExamManagementTab';
import { ExamBookingsTab }        from '../components/admin/ExamBookingsTab';

// RBAC — mirrors backend ROLE_TABS exactly
const ROLE_TABS = {
  super_admin:  null,
  jobs_manager: new Set(['jobs']),
  support:      new Set(['users', 'tickets', 'agents']),
  finance:      new Set(['payments', 'revenue', 'enroll']),
  content:      new Set(['courses', 'settings', 'blueprint', 'new_course_guide', 'broadcast', 'email_broadcast']),
  analytics:    new Set(['analytics', 'email_activity', 'activity', 'profile_stats']),
};

// Tab display labels
const TAB_LABELS = {
  users: 'Users', payments: 'Payments', enroll: 'Enroll', revenue: 'Revenue',
  analytics: 'Analytics', poster: 'Promo Poster', achievement: 'Achievement',
  courses: 'Courses', server: 'Server', activity: 'Activity', tickets: 'Tickets',
  agents: 'Agents', security: 'Security', network: 'Network',
  projects_mgmt: 'Projects', settings: 'Announcements', broadcast: 'Broadcast',
  email_activity: 'Email Activity', blueprint: 'Blueprint',
  new_course_guide: 'New Course Guide', health_monitor: 'Health Monitor',
  certificates: 'Certificates', jobs: 'Jobs', profile_stats: 'Profiles',
  email_broadcast: 'Email Blast', performance: 'Performance', team: 'Team',
  exam_management: 'Exam Management', exam_bookings: 'Exam Bookings',
};

// Stats cards only on these tabs
const STATS_TABS = new Set(['users', 'payments', 'enroll', 'revenue']);

// Render the correct tab component
const TabContent = ({ activeTab, fetchStats, adminRole }) => {
  switch (activeTab) {
    case 'users':            return <UsersTab onStatsRefresh={fetchStats} />;
    case 'payments':         return <PaymentsTab />;
    case 'enroll':           return <EnrollTab onStatsRefresh={fetchStats} />;
    case 'revenue':          return <RevenueTab />;
    case 'analytics':        return <AnalyticsTab />;
    case 'poster':           return <PromoPosterTab />;
    case 'achievement':      return <AchievementPosterTab />;
    case 'courses':          return <CoursesTab />;
    case 'server':           return <ServerTab />;
    case 'activity':         return <ActivityTab />;
    case 'tickets':          return <TicketsTab />;
    case 'agents':           return <AgentsTab />;
    case 'security':         return <SecurityTab />;
    case 'network':          return <NetworkTab token={getAdminToken()} />;
    case 'projects_mgmt':    return <ProjectsTab />;
    case 'settings':         return <AnnouncementTab />;
    case 'broadcast':        return <BroadcastTab />;
    case 'email_activity':   return <EmailActivityTab />;
    case 'blueprint':        return <BlueprintTab />;
    case 'new_course_guide': return <NewCourseGuideTab />;
    case 'health_monitor':   return <HealthMonitorTab />;
    case 'certificates':     return <CertificateCatalogTab />;
    case 'jobs':             return <JobsTab />;
    case 'profile_stats':    return <ProfileStatsTab />;
    case 'email_broadcast':  return <EmailBroadcastTab />;
    case 'performance':      return <PerformanceDiagnosticTab />;
    case 'team':             return adminRole === 'super_admin' ? <TeamTab /> : null;
    case 'exam_management':  return adminRole === 'super_admin' ? <ExamManagementTab /> : null;
    case 'exam_bookings':    return adminRole === 'super_admin' ? <ExamBookingsTab /> : null;
    default:
      return (
        <div className="text-slate-400 py-20 text-center">
          Select a section from the sidebar.
        </div>
      );
  }
};

// ── Main Dashboard component ──────────────────────────────────────────────────
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  const [adminRole, setAdminRole] = useState(getAdminRole);
  const [adminName, setAdminName] = useState(getAdminName);
  const allowedTabs = ROLE_TABS[adminRole] ?? null;

  const [activeTab, setActiveTab] = useState(() => {
    const tabs = ROLE_TABS[getAdminRole()];
    return tabs ? [...tabs][0] : 'users';
  });

  // Sidebar collapse — persisted
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('adminSidebarCollapsed') === 'true'; }
    catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('adminSidebarCollapsed', String(collapsed)); }
    catch {}
  }, [collapsed]);

  // Verify admin session on mount
  useEffect(() => {
    const verifyAdmin = async () => {
      const token = getAdminToken();
      if (!token) { navigate('/admin'); return; }
      try {
        const res = await fetch(`${API_BASE}/api/admin/verify`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) {
          ['adminToken', 'adminRole', 'adminName'].forEach(k => localStorage.removeItem(k));
          navigate('/admin');
          return;
        }
        const data = await res.json();
        if (data.role) { localStorage.setItem('adminRole', data.role); setAdminRole(data.role); }
        if (data.name) { localStorage.setItem('adminName', data.name); setAdminName(data.name); }
      } catch {
        ['adminToken', 'adminRole', 'adminName'].forEach(k => localStorage.removeItem(k));
        navigate('/admin');
      }
    };
    verifyAdmin();
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchStats(); }, []);

  const handleLogout = () => {
    ['adminToken', 'adminRole', 'adminName'].forEach(k => localStorage.removeItem(k));
    toast.success('Logged out');
    navigate('/admin');
  };

  const handleNav = useCallback((tab) => {
    if (allowedTabs !== null && !allowedTabs.has(tab)) return;
    setActiveTab(tab);
  }, [allowedTabs]);

  const sidebarOffset = collapsed ? 'lg:pl-[68px]' : 'lg:pl-[260px]';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-850 to-slate-900">

      {/* Sidebar */}
      <AdminSidebar
        activeTab={activeTab}
        onNav={handleNav}
        onLogout={handleLogout}
        adminRole={adminRole}
        adminName={adminName}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      {/* Mobile hamburger */}
      <MobileMenuButton onClick={() => setMobileOpen(true)} />

      {/* Main content — shifts right based on sidebar width */}
      <div className={`transition-[padding-left] duration-200 ease-in-out ${sidebarOffset}`}>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pt-16 lg:pt-6">

          {/* Page heading */}
          <div className="mb-6">
            <h1 className="text-xl font-bold text-white">
              {TAB_LABELS[activeTab] || activeTab}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">GRRAS Learning Labs · Admin Panel</p>
          </div>

          {/* Stats cards — shown only on People-group tabs */}
          {stats && STATS_TABS.has(activeTab) && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              {[
                { label: 'Total Users',  value: stats.total_users,        icon: Users,     color: 'blue' },
                { label: 'Active',       value: stats.active_users,       icon: UserCheck, color: 'emerald' },
                { label: 'Disabled',     value: stats.disabled_users,     icon: UserX,     color: 'red' },
                { label: 'Enrollments',  value: stats.total_enrollments,  icon: BookOpen,  color: 'amber' },
                { label: 'Payments',     value: stats.completed_payments, icon: CreditCard,color: 'purple' },
                {
                  label: 'Paying Users',
                  value: stats.unique_payers,
                  icon: Wallet, color: 'cyan',
                  testid: 'stat-unique-payers',
                  subtitle: stats.total_users > 0
                    ? `${Math.round((stats.unique_payers / stats.total_users) * 100)}% conversion`
                    : null,
                },
              ].map((s, i) => (
                <Card key={i} className={`${colorMap[s.color].card} backdrop-blur`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-xs ${colorMap[s.color].text}`}>{s.label}</p>
                        <p className="text-2xl font-bold text-white" data-testid={s.testid}>
                          {s.value}
                        </p>
                        {s.subtitle && (
                          <p className={`text-[10px] mt-1 ${colorMap[s.color].text} opacity-80`}
                            data-testid={s.testid ? `${s.testid}-subtitle` : undefined}>
                            {s.subtitle}
                          </p>
                        )}
                      </div>
                      <div className={colorMap[s.color].iconBg}>
                        <s.icon className={`w-5 h-5 ${colorMap[s.color].icon}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Tab content */}
          <TabContent
            activeTab={activeTab}
            fetchStats={fetchStats}
            adminRole={adminRole}
          />

        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
